// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { tasksStrings } from './strings';
import type { TaskSelection } from './types';

export interface TasksSelectionToolbarProps {
  readonly selection: TaskSelection;
  readonly onBulkComplete: () => void;
  readonly onBulkDelete: () => void;
  readonly onBulkMove: () => void;
  readonly onClear: () => void;
}

/**
 * Bottom-anchored toolbar shown only while selection mode is `active`.
 * Returns `null` otherwise so the consumer can render us unconditionally.
 */
export const TasksSelectionToolbar = React.memo<TasksSelectionToolbarProps>((props) => {
  const { selection, onBulkComplete, onBulkDelete, onBulkMove, onClear } = props;
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  if (selection.mode !== 'active') {
    return null;
  }

  const count = selection.selectedIds.size;
  const disabled = count === 0;

  return (
    <View
      style={[
        styles.bar,
        {
          backgroundColor: theme.colors.surface.secondary,
          borderTopColor: theme.colors.border.secondary,
        },
      ]}
      accessibilityLabel={tasksStrings.selection.countLabel(count)}
    >
      <Text style={[styles.count, { color: theme.colors.text.primary }]}>
        {tasksStrings.selection.countLabel(count)}
      </Text>
      <View style={styles.actions}>
        <ToolbarButton
          label={tasksStrings.selection.bulkComplete}
          color={theme.colors.accent.indigo}
          disabled={disabled}
          onPress={onBulkComplete}
        />
        <ToolbarButton
          label={tasksStrings.selection.bulkMove}
          color={theme.colors.text.primary}
          disabled={disabled}
          onPress={onBulkMove}
        />
        <ToolbarButton
          label={tasksStrings.selection.bulkDelete}
          color={theme.colors.accent.rose}
          disabled={disabled}
          onPress={onBulkDelete}
        />
        <ToolbarButton
          label={tasksStrings.selection.exit}
          color={theme.colors.text.secondary}
          disabled={false}
          onPress={onClear}
        />
      </View>
    </View>
  );
});

TasksSelectionToolbar.displayName = 'TasksSelectionToolbar';

interface ToolbarButtonProps {
  readonly label: string;
  readonly color: string;
  readonly disabled: boolean;
  readonly onPress: () => void;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ label, color, disabled, onPress }) => (
  <Pressable
    onPress={() => {
      if (!disabled) {
        onPress();
      }
    }}
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilityState={{ disabled }}
    disabled={disabled}
    style={({ pressed }) => [styles.button, pressed && !disabled && { opacity: 0.7 }]}
  >
    <Text
      style={[
        styles.buttonText,
        {
          color,
          opacity: disabled ? 0.4 : 1,
        },
      ]}
    >
      {label}
    </Text>
  </Pressable>
);

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'column',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  count: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
  } satisfies TextStyle,
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  } satisfies ViewStyle,
  button: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginRight: 8,
  } satisfies ViewStyle,
  buttonText: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
});
